import { Injectable, Type } from '@angular/core';
import { BaseApiService } from '@kinet-open-network/client/core/api-client';
import { plainToInstance } from 'class-transformer';
import { formatISO } from 'date-fns';
import { map, of } from 'rxjs';
import { MoveCustomerFeeData } from '../models/api/move-fee.model';
import {
  encodeInvoicingPeriod,
  SetInvoicingPeriod,
} from '../models/api/set-invoicing-period.model';
import { CustomerFee } from '../models/customer-fee.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerFeesService extends BaseApiService<CustomerFee> {
  override model: Type<CustomerFee> = CustomerFee;
  override get endpoint(): string {
    return '/customer-fees';
  }

  getFees(customerId?: number | null, apId?: string | null) {
    if (!apId || !customerId) return of([] as CustomerFee[]);
    return this.http
      .get<CustomerFee[]>(`${this.endpoint}/${customerId}/${apId}`)
      .pipe(map((fees) => plainToInstance(CustomerFee, fees)));
  }

  setFeeActivationDate(id: string, activationDate: Date | null) {
    return this.http.put<CustomerFee>(
      `${this.endpoint}/${id}/activation-date`,
      {
        activationDate: activationDate
          ? formatISO(activationDate, { representation: 'date' })
          : null,
      },
    );
  }

  setFeeDeactivationDate(id: string, deactivationDate: Date | null) {
    return this.http.put<CustomerFee>(
      `${this.endpoint}/${id}/deactivation-date`,
      {
        deactivationDate: deactivationDate
          ? formatISO(deactivationDate, { representation: 'date' })
          : null,
      },
    );
  }

  changeCustomerFeeApId(id: string, data: MoveCustomerFeeData) {
    return this.http
      .put<CustomerFee>(`${this.endpoint}/${id}/change-ap`, data)
      .pipe(map((fee) => plainToInstance(CustomerFee, fee)));
  }

  setInvoicingPeriod(id: string, data: SetInvoicingPeriod) {
    return this.http
      .put<CustomerFee>(
        `${this.endpoint}/${id}/invoicing/period`,
        encodeInvoicingPeriod(data),
      )
      .pipe(map((fee) => plainToInstance(CustomerFee, fee)));
  }

  setInvoiceState(id: string, invoiceDate: Date | null) {
    return this.http
      .put<CustomerFee>(`${this.endpoint}/${id}/invoicing/state`, {
        invoiceDate: invoiceDate
          ? formatISO(invoiceDate, { representation: 'date' })
          : null,
      })
      .pipe(map((fee) => plainToInstance(CustomerFee, fee)));
  }

  updateState(
    id: string,
    data: Partial<{
      activationDate: Date | null;
      deactivationDate: Date | null;
    }>,
  ) {
    return this.http
      .put<CustomerFee>(`${this.endpoint}/${id}/state`, {
        ...data,
        activationDate: data.activationDate
          ? formatISO(data.activationDate, { representation: 'date' })
          : null,
        deactivationDate: data.deactivationDate
          ? formatISO(data.deactivationDate, { representation: 'date' })
          : null,
      })
      .pipe(map((fee) => plainToInstance(CustomerFee, fee)));
  }
}
